import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "../../config.js";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Typography, Paper, CircularProgress } from "@mui/material";
import NavMenu from "../../components/navbar/NavMenu";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import SubTitle from "../../components/common/SubTitle";
import ScheduleDetailsForm from "./ScheduleDetailsForm";
import StopSchedulesTable from "./StopSchedulesTable";
import ActionButtons from "./ActionButtons";
import Footer from "../../components/common/Footer.jsx";
import {
  buildLocalScheduleConflictMessage,
  buildScheduleConflictMessage,
  findScheduleConflictFromList,
  getApiErrorResponse,
  getGenericApiErrorMessage,
} from "../../utils/scheduleUi";
import {
  applyTravelDurationsToStopSchedules,
  durationsForOrderedStops,
  orderStopSchedulesByTimeline,
  reverseScheduleStopOrder,
} from "../../utils/stopScheduleTimeline";

async function createSchedule(schedule) {
  const response = await axios.post(`${config.baseURL}/schedule`, schedule, {
    withCredentials: true,
  });
  return response.data;
}

async function createStopSchedules(scheduleId, stopSchedules) {
  const response = await axios.post(
    `${config.baseURL}/schedule/stop`,
    { scheduleId, stopSchedules },
    { withCredentials: true },
  );
  return response.data;
}

async function updateSchedule(schedule) {
  try {
    console.log("this is schedule", schedule);
    const response = await axios.put(`${config.baseURL}/schedule`, schedule, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function updateStopSchedules(stopSchedules) {
  try {
    console.log("this is stopSchedules", stopSchedules);
    const response = await axios.put(
      `${config.baseURL}/schedule/stop`,
      stopSchedules,
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getScheduleById(id) {
  try {
    const response = await axios.get(`${config.baseURL}/schedule/id`, {
      params: { scheduleId: id },
      withCredentials: true,
    });
    console.log("Schedule by ID:", response.data);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getAllRoutes() {
  try {
    const response = await axios.get(`${config.baseURL}/route/organization`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function getAllVehicles() {
  try {
    const response = await axios.get(`${config.baseURL}/vehicle/organization`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function getOrganizationSchedules(organizationId) {
  if (!organizationId) {
    return [];
  }

  try {
    const response = await axios.get(
      `${config.baseURL}/schedule/organization`,
      {
        params: { organizationId },
        withCredentials: true,
      },
    );

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching organization schedules:", error);
    return [];
  }
}

async function getStopByRouteCoordinate(longitude, latitude, organizationId) {
  try {
    const response = await axios.get(
      `${config.baseURL}/stop/organization/location`,
      {
        params: { longitude, latitude, organizationId },
        withCredentials: true,
      },
    );
    const stopData = response.data;

    if (Array.isArray(stopData)) {
      return stopData[0] || null;
    }

    return stopData || null;
  } catch (error) {
    console.error("Failed to fetch organization locations", error);
    return null;
  }
}

async function getStopById(id) {
  try {
    const response = await axios.get(`${config.baseURL}/stop/id?id=${id}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function getRouteStopTravelSegments(routeNumericId) {
  const response = await axios.get(
    `${config.baseURL}/route/stop-travel-segments`,
    {
      params: { id: routeNumericId },
      withCredentials: true,
    },
  );
  return response.data;
}

async function getFormattedDate(stopSchedules) {
  try {
    const formattedStopSchedules = await Promise.all(
      stopSchedules.map(async (stopSchedule) => {
        if (stopSchedule?.stop?.name || stopSchedule?.stopName) {
          return {
            ...stopSchedule,
            stopName: stopSchedule.stop?.name || stopSchedule.stopName,
          };
        }

        if (!stopSchedule?.stopId) {
          return {
            ...stopSchedule,
            stopName: "",
          };
        }

        const stop = await getStopById(stopSchedule.stopId);

        return {
          ...stopSchedule,
          stopName: stop?.name || "",
        };
      }),
    );

    console.log(formattedStopSchedules, "formattedStopSchedules");
    return formattedStopSchedules;
  } catch (err) {
    console.error("Error in getting formatted stop schedules:", err);
    return [];
  }
}

async function getStopDetailsFromRouteCoordinate(
  routeCoordinate,
  organizationId,
) {
  if (routeCoordinate?.stopId) {
    return getStopById(routeCoordinate.stopId);
  }

  return getStopByRouteCoordinate(
    routeCoordinate.longitude,
    routeCoordinate.latitude,
    organizationId,
  );
}

function getOrderedRouteCoordinates(routeCoordinates = []) {
  return [...routeCoordinates].sort((firstCoordinate, secondCoordinate) => {
    const firstOrder = Number(firstCoordinate?.order);
    const secondOrder = Number(secondCoordinate?.order);

    if (Number.isNaN(firstOrder) || Number.isNaN(secondOrder)) {
      return 0;
    }

    return firstOrder - secondOrder;
  });
}

function mapTravelStopsToScheduleRows(orderedStops = []) {
  return orderedStops.map((stop, index) => ({
    order: index + 1,
    longitude: stop.longitude,
    latitude: stop.latitude,
    arrivalTime: "",
    departureTime: "",
    stopId: stop.stopId,
    stopName: stop.stopName,
  }));
}

function canApplyTravelTimes(stops, durations) {
  return (
    Array.isArray(stops) &&
    stops.length > 1 &&
    Array.isArray(durations) &&
    durations.length === stops.length - 1
  );
}

const getRouteVehicleId = (routeVehicle) => {
  const candidateId =
    routeVehicle?.id ??
    routeVehicle?.vehicleId ??
    routeVehicle?.vehicle?.id ??
    routeVehicle?.Id ??
    routeVehicle?.vehicle?.Id;

  const normalizedId = Number(candidateId);
  return Number.isNaN(normalizedId) ? null : normalizedId;
};

function getVehicleIdsFromScheduleResponse(scheduleData) {
  const fromJoin = scheduleData?.vehicleSchedule;
  if (Array.isArray(fromJoin) && fromJoin.length > 0) {
    const ids = fromJoin
      .map((vs) => vs.vehicleId ?? vs.vehicle?.id)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length > 0) {
      return ids;
    }
  }
  const summary = scheduleData?.vehicleScheduleSummary;
  if (Array.isArray(summary) && summary.length > 0) {
    return summary
      .map((s) => s.vehicleId)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }
  return [];
}

const getRouteAssignedVehicleIds = (route, allVehicles = []) => {
  const normalizedRouteId = Number(route?.id ?? route?.routeId);

  if (Number.isNaN(normalizedRouteId) || !normalizedRouteId) {
    return new Set();
  }

  const routeVehicleIds = new Set();

  if (Array.isArray(route?.vehicles)) {
    route.vehicles.forEach((routeVehicle) => {
      const vehicleId = getRouteVehicleId(routeVehicle);

      if (vehicleId) {
        routeVehicleIds.add(vehicleId);
      }
    });
  }

  allVehicles.forEach((vehicle) => {
    const vehicleId = Number(vehicle?.id);
    const vehicleRouteId = Number(
      vehicle?.routeId ??
        vehicle?.route?.id ??
        vehicle?.route?.routeId ??
        vehicle?.route?.Id,
    );

    if (
      !Number.isNaN(vehicleId) &&
      !Number.isNaN(vehicleRouteId) &&
      vehicleRouteId === normalizedRouteId
    ) {
      routeVehicleIds.add(vehicleId);
    }
  });

  return routeVehicleIds;
};

// Utility function to convert HH:mm to ISO datetime
const formatTimeToISO = (timeStr, baseDate = new Date(), dayOffset = 0) => {
  if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date(baseDate);
  date.setDate(date.getDate() + dayOffset);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

const getDateParts = (dateStr) => {
  if (!dateStr) return null;

  const normalizedDate = dateStr.includes("T")
    ? dateStr.split("T")[0]
    : dateStr;
  const [year, month, day] = normalizedDate.split("-").map(Number);

  if (!year || !month || !day) return null;

  return { year, month, day };
};

const getLocalBaseDate = (dateStr) => {
  const dateParts = getDateParts(dateStr);

  if (!dateParts) {
    return new Date();
  }

  return new Date(dateParts.year, dateParts.month - 1, dateParts.day);
};

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateToUTCISOString = (dateStr) => {
  const dateParts = getDateParts(dateStr);

  if (!dateParts) {
    return null;
  }

  return new Date(
    Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, 0, 0, 0, 0),
  ).toISOString();
};

// Utility function to parse ISO or HH:mm to Date object
const parseTimeToDate = (timeStr, baseDate = new Date()) => {
  if (!timeStr) return null;
  if (timeStr.includes("T")) {
    return new Date(timeStr);
  }
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date(baseDate);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const getMinutesFromTime = (timeStr) => {
  if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
};

const getTimelineMinutes = (timeStr, previousMinutes = null) => {
  const minutes = getMinutesFromTime(timeStr);
  if (minutes == null) return null;

  if (previousMinutes == null) return minutes;

  let timelineMinutes = minutes;
  while (timelineMinutes < previousMinutes) {
    timelineMinutes += 24 * 60;
  }

  return timelineMinutes;
};

const getTripDurationMinutes = (startTime, endTime) => {
  const startMinutes = getTimelineMinutes(startTime);
  const endMinutes = getTimelineMinutes(endTime, startMinutes);

  if (startMinutes == null || endMinutes == null) return null;

  return endMinutes - startMinutes;
};

const normalizeApiTimeToHHmm = (timeStr) => {
  if (!timeStr) return "";
  if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;

  const parsedDate = parseTimeToDate(timeStr);
  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const getScheduleBoundaryTimes = (stopSchedules = [], schedule = {}) => {
  const firstStop = stopSchedules[0];
  const lastStop = stopSchedules[stopSchedules.length - 1];

  return {
    startTime:
      firstStop?.departureTime ||
      firstStop?.arrivalTime ||
      schedule.startTime ||
      "",
    endTime:
      lastStop?.arrivalTime ||
      lastStop?.departureTime ||
      schedule.endTime ||
      "",
  };
};

const getRepeatEveryValue = (repitation) => {
  return repitation ? 1 : 0;
};

const buildResolvedStopSchedules = (stopSchedules = [], schedule = {}) => {
  return stopSchedules.map((stopSchedule, index) => {
    const isFirstStop = index === 0;
    const isLastStop = index === stopSchedules.length - 1;

    if (isFirstStop) {
      const boundaryTime =
        stopSchedule.departureTime ||
        stopSchedule.arrivalTime ||
        schedule.startTime ||
        "";

      return {
        ...stopSchedule,
        arrivalTime: boundaryTime,
        departureTime: boundaryTime,
      };
    }

    if (isLastStop) {
      const boundaryTime =
        stopSchedule.arrivalTime ||
        stopSchedule.departureTime ||
        schedule.endTime ||
        "";

      return {
        ...stopSchedule,
        arrivalTime: boundaryTime,
        departureTime: boundaryTime,
      };
    }

    return {
      ...stopSchedule,
      arrivalTime: stopSchedule.arrivalTime || "",
      departureTime: stopSchedule.departureTime || "",
    };
  });
};

const buildSchedulePayload = (schedule, startTime, endTime, baseDate) => {
  const startMinutes = getTimelineMinutes(startTime);
  const endMinutes = getTimelineMinutes(endTime, startMinutes);
  const endDayOffset =
    startMinutes != null && endMinutes != null
      ? Math.floor(endMinutes / (24 * 60))
      : 0;

  const payload = {
    name: schedule.name,
    description: schedule.description,
    startTime: formatTimeToISO(startTime, baseDate),
    endTime: formatTimeToISO(endTime, baseDate, endDayOffset),
    repitation: schedule.repitation,
    vehicleIds: Array.isArray(schedule.vehicleIds)
      ? schedule.vehicleIds.map(Number)
      : [],
    routeId: Number(schedule.routeId),
    startDate: formatDateToUTCISOString(schedule.startDate),
    endCondition: toEndConditionEnum(schedule.endCondition),
    endDate:
      schedule.endCondition === "byDate"
        ? formatDateToUTCISOString(schedule.endDate)
        : null,
    occurrences:
      schedule.endCondition === "afterOccurrences"
        ? Number(schedule.occurrences)
        : null,
    repeatEvery: Number(schedule.repeatEvery) || 1,
    weeklyDays:
      schedule.repitation === "WEEKLY" ? schedule.weeklyDays || [] : null,
    monthlyType:
      schedule.repitation === "MONTHLY"
        ? toMonthlyTypeEnum(schedule.monthlyType)
        : null,
    monthlyDay:
      schedule.repitation === "MONTHLY" && schedule.monthlyType === "dayOfMonth"
        ? Number(schedule.monthlyDay)
        : null,
    monthlyWeekOrder:
      schedule.repitation === "MONTHLY" &&
      schedule.monthlyType === "dayOfWeekOfMonth"
        ? toUpperEnum(schedule.monthlyWeekOrder)
        : null,
    monthlyWeekDay:
      schedule.repitation === "MONTHLY" &&
      schedule.monthlyType === "dayOfWeekOfMonth"
        ? toUpperEnum(schedule.monthlyWeekDay)
        : null,
  };

  if (schedule.id) {
    payload.id = schedule.id;
  }

  return payload;
};

const getNormalizedStopTimes = (stopSchedule, index, stopSchedules) => {
  const isFirstStop = index === 0;
  const isLastStop = index === stopSchedules.length - 1;

  return {
    arrivalTime: isFirstStop
      ? stopSchedule.departureTime
      : stopSchedule.arrivalTime,
    departureTime: isLastStop
      ? stopSchedule.arrivalTime
      : stopSchedule.departureTime,
  };
};

const hasCompleteStopSchedule = (stopSchedule, index, stopSchedules) => {
  const isFirstStop = index === 0;
  const isLastStop = index === stopSchedules.length - 1;
  const arrivalTime = isFirstStop
    ? stopSchedule.departureTime
    : stopSchedule.arrivalTime;
  const departureTime = isLastStop
    ? stopSchedule.arrivalTime
    : stopSchedule.departureTime;

  return (
    !!stopSchedule.stopId &&
    !!arrivalTime &&
    /^\d{2}:\d{2}$/.test(arrivalTime) &&
    !!departureTime &&
    /^\d{2}:\d{2}$/.test(departureTime)
  );
};

const getRequiredStopTimeFields = (index, totalStops) => {
  const isFirstStop = index === 0;
  const isLastStop = index === totalStops - 1;

  if (isFirstStop && isLastStop) {
    return ["departureTime"];
  }

  if (isFirstStop) {
    return ["departureTime"];
  }

  if (isLastStop) {
    return ["arrivalTime"];
  }

  return ["arrivalTime", "departureTime"];
};

const toEndConditionEnum = (val) => {
  const map = {
    noEnd: "NO_END",
    byDate: "BY_DATE",
    afterOccurrences: "AFTER_OCCURRENCES",
  };
  return map[val] ?? "NO_END";
};
const toMonthlyTypeEnum = (val) => {
  const map = {
    dayOfMonth: "DAY_OF_MONTH",
    dayOfWeekOfMonth: "DAY_OF_WEEK_OF_MONTH",
  };
  return map[val] ?? null;
};
const toUpperEnum = (val) => (val ? val.toUpperCase() : null);
// Add reverse-mapping helpers:
const fromEndConditionEnum = (val) => {
  const map = {
    NO_END: "noEnd",
    BY_DATE: "byDate",
    AFTER_OCCURRENCES: "afterOccurrences",
  };
  return map[val] ?? "noEnd";
};
const fromMonthlyTypeEnum = (val) => {
  const map = {
    DAY_OF_MONTH: "dayOfMonth",
    DAY_OF_WEEK_OF_MONTH: "dayOfWeekOfMonth",
  };
  return map[val] ?? "dayOfMonth";
};
const toLowerEnum = (val) => (val ? val.toLowerCase() : null);
const AddSchedule = ({
  embedded = false,
  embeddedScheduleId,
  onCancel,
  onComplete,
  onDelete,
} = {}) => {
  const { t } = useTranslation();
  const { scheduleId: paramScheduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const updateRoute = location.state?.updateRoute;

  // Get the mode from location state (view or edit)
  const mode = location.state?.mode || "edit";
  const isViewMode = mode === "view";

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;
  const stateScheduleId = location.state?.id?.scheduleId;
  const effectiveScheduleId = embeddedScheduleId || paramScheduleId || stateScheduleId;
  const closeForm = () => {
    if (embedded) {
      onCancel?.();
      return;
    }
    navigate(schedulesListPath);
  };
  const finishForm = () => {
    if (embedded) {
      onComplete?.();
      return;
    }
    navigate(schedulesListPath);
  };
  const isSchoolApp =
    localStorage.getItem("selectedApp") === "schoolTrack" ||
    location.pathname.startsWith("/school-schedules");
  const schedulesListPath = isSchoolApp ? "/school-schedules" : "/schedule";
  const NavigationMenu = isSchoolApp ? SchoolBusNavMenu : NavMenu;
  // console.log(location.state, "location state");

  const [initialScheduleName, setInitialScheduleName] = useState("Schedule");

  const [schedule, setSchedule] = useState({
    name: "",
    description: "",
    startTime: "", // Store as HH:mm
    endTime: "", // Store as HH:mm
    repitation: "",
    repeatEvery: 1,
    occurrences: 0,
    endCondition: "noEnd",
    organizationId: orgId,
    vehicleIds: [],
    routeId: location.state?.routeId || "",
    startDate: "",
    endDate: "",
    weeklyDays: [],
    monthlyType: "dayOfMonth",
    monthlyDay: 1,
    monthlyWeekOrder: "first",
    monthlyWeekDay: "sunday",
  });
  const [stopSchedules, setStopSchedules] = useState([]);
  const [segmentDurationsSeconds, setSegmentDurationsSeconds] = useState([]);
  const [travelTimeError, setTravelTimeError] = useState("");
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const existingSchedulesRef = useRef([]);
  const { startTime: boundaryStartTime, endTime: boundaryEndTime } =
    getScheduleBoundaryTimes(stopSchedules, schedule);

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  // Update the path based on mode
const path = updateRoute
  ? `${orgName || "organization"} > Routes > Update > Assign Schedules`
  : effectiveScheduleId
    ? isViewMode
      ? `${orgName || "organization"} > Schedules > ${initialScheduleName} > View`
      : `${orgName || "organization"} > Schedules > ${initialScheduleName}`
    : `${orgName || "organization"} > Schedules > Create`;

  const resolveStopsFromRouteCoordinates = async (route) => {
    const orderedRouteCoordinates = getOrderedRouteCoordinates(
      route.routeCoordinates,
    );
    const stopPromises = orderedRouteCoordinates.map(async (coord) => {
      const stopInfo = await getStopDetailsFromRouteCoordinate(
        coord,
        route.organizationId,
      );
      if (!stopInfo) {
        return null;
      }
      return {
        longitude: coord.longitude,
        latitude: coord.latitude,
        arrivalTime: "",
        departureTime: "",
        stopId: stopInfo.id,
        stopName: stopInfo.name,
      };
    });
    const resolvedStops = await Promise.all(stopPromises);
    return resolvedStops
      .filter((stop) => stop !== null)
      .map((stop, index) => ({ ...stop, order: index + 1 }));
  };

  const loadStopsForSelectedRoute = async (
    route,
    startTime,
    { applyTravelTimes } = { applyTravelTimes: true },
  ) => {
    setTravelTimeError("");
    setSegmentDurationsSeconds([]);

    if (!route) {
      setStopSchedules([]);
      return;
    }

    let travel = null;
    try {
      travel = await getRouteStopTravelSegments(route.id);
    } catch (error) {
      console.error("Error fetching route travel segments:", error);
    }

    let nextStops = travel?.orderedStops?.length
      ? mapTravelStopsToScheduleRows(travel.orderedStops)
      : await resolveStopsFromRouteCoordinates(route);

    const durations = (travel?.segments || []).map(
      (segment) => segment.durationSeconds,
    );
    setSegmentDurationsSeconds(durations);

    const normalizedStartTime =
      startTime && /^\d{2}:\d{2}$/.test(startTime) ? startTime : "";

    if (
      applyTravelTimes &&
      normalizedStartTime &&
      canApplyTravelTimes(nextStops, durations)
    ) {
      const timeline = applyTravelDurationsToStopSchedules(
        nextStops,
        normalizedStartTime,
        durations,
      );
      nextStops = timeline.stopSchedules;
      setSchedule((prevSchedule) => ({
        ...prevSchedule,
        startTime: normalizedStartTime,
        endTime: timeline.endTime,
      }));
    } else if (nextStops.length > 0 && normalizedStartTime) {
      nextStops = nextStops.map((stop, index) =>
        index === 0
          ? {
              ...stop,
              arrivalTime: normalizedStartTime,
              departureTime: normalizedStartTime,
            }
          : stop,
      );
    }

    if (
      applyTravelTimes &&
      nextStops.length > 1 &&
      !canApplyTravelTimes(nextStops, durations)
    ) {
      setTravelTimeError(
        t(
          "Could not calculate real travel time between stops. Enter times manually or reselect the route.",
        ),
      );
    }

    setStopSchedules(nextStops);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [routesData, vehiclesData, schedulesData] = await Promise.all([
          getAllRoutes(),
          getAllVehicles(),
          getOrganizationSchedules(orgId),
        ]);
        setRoutes(routesData);
        setVehicles(vehiclesData);
        existingSchedulesRef.current = schedulesData;

        if (effectiveScheduleId) {
          const scheduleData = await getScheduleById(effectiveScheduleId);
          setSchedule({
            ...scheduleData,
            startTime: scheduleData.startTime
              ? new Date(scheduleData.startTime).toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
              : "",
            endTime: scheduleData.endTime
              ? new Date(scheduleData.endTime).toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
              : "",
            startDate: scheduleData.startDate
              ? new Date(scheduleData.startDate).toISOString().split("T")[0]
              : "",
            endCondition: fromEndConditionEnum(scheduleData.endCondition),
            endDate: scheduleData.endDate
              ? new Date(scheduleData.endDate).toISOString().split("T")[0]
              : "",
            weeklyDays: scheduleData.weeklyDays || [],
            monthlyType: fromMonthlyTypeEnum(scheduleData.monthlyType),
            monthlyWeekOrder: toLowerEnum(scheduleData.monthlyWeekOrder),
            monthlyWeekDay: toLowerEnum(scheduleData.monthlyWeekDay),
            monthlyDay: scheduleData.monthlyDay || 1,
            repeatEvery:
              scheduleData.repeatEvery ??
              getRepeatEveryValue(scheduleData.repitation),
            occurrences: scheduleData.occurrences ?? 0,
            vehicleIds: getVehicleIdsFromScheduleResponse(scheduleData),
          });
          setInitialScheduleName(scheduleData.name || "Schedule");
          const loadedStartTime = scheduleData.startTime
            ? new Date(scheduleData.startTime).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
            : "";
          const formattedStopSchedules = await getFormattedDate(
            scheduleData.stopSchedules || [],
          );
          const orderedStopSchedules = orderStopSchedulesByTimeline(
            formattedStopSchedules.map((stop) => ({
              ...stop,
              arrivalTime: normalizeApiTimeToHHmm(stop.arrivalTime),
              departureTime: normalizeApiTimeToHHmm(stop.departureTime),
            })),
            loadedStartTime,
          );
          setStopSchedules(orderedStopSchedules);
          setSelectedRoute(
            routesData.find((r) => r.id === scheduleData.routeId) || {
              id: scheduleData.routeId,
            },
          );
          try {
            const travel = await getRouteStopTravelSegments(scheduleData.routeId);
            const orderedDurations = durationsForOrderedStops(
              orderedStopSchedules,
              travel?.segments || [],
            );
            setSegmentDurationsSeconds(
              orderedDurations ||
                (travel?.segments || []).map((segment) => segment.durationSeconds),
            );
          } catch (error) {
            console.error("Error fetching route travel segments:", error);
            setSegmentDurationsSeconds([]);
          }
        } else if (location.state?.routeId || location.state?.id?.routeId) {
          // Modified this section to handle both cases
          const routeId = parseInt(
            location.state?.routeId || location.state?.id?.routeId,
          );
          setSchedule((prev) => ({ ...prev, routeId }));
          const selectedRoute = routesData.find((r) => r.id === routeId);

          if (selectedRoute) {
            setSelectedRoute(selectedRoute);
            await loadStopsForSelectedRoute(selectedRoute, schedule.startTime, {
              applyTravelTimes: true,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [effectiveScheduleId, location.state, orgId]);

  const handleChange = async (e) => {
    if (isViewMode) return;

    let { name, value } = e.target;
    const isRouteChange = name === "routeId";

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: undefined,
      submit: undefined,
    }));
    if (isRouteChange) {
      value = parseInt(value);
    }
    const nextSelectedRoute = isRouteChange
      ? routes.find((route) => route.id === value) || null
      : selectedRoute;
    const nextVehicleIds = isRouteChange
      ? schedule.vehicleIds.filter((vehicleId) =>
          getRouteAssignedVehicleIds(nextSelectedRoute, vehicles).has(
            Number(vehicleId),
          ),
        )
      : schedule.vehicleIds;

    setSchedule((prevSchedule) => ({
      ...prevSchedule,
      [name]: value,
      ...(isRouteChange
        ? {
            vehicleIds: nextVehicleIds,
          }
        : {}),
      ...(name === "repitation"
        ? {
            repeatEvery: getRepeatEveryValue(value),
          }
        : {}),
    }));

    if (isRouteChange) {
      setSelectedRoute(nextSelectedRoute);
    }

    if (isRouteChange && value) {
      setLoading(true);
      try {
        await loadStopsForSelectedRoute(nextSelectedRoute, schedule.startTime, {
          applyTravelTimes: true,
        });
      } catch (error) {
        console.error("Error fetching stops:", error);
      } finally {
        setLoading(false);
      }
    } else if (isRouteChange) {
      setStopSchedules([]);
      setSegmentDurationsSeconds([]);
      setTravelTimeError("");
    }
  };

  const handleVehicleChange = (e) => {
    if (isViewMode) return;

    const selectedOptions = e.target.value.map(Number);
    console.log("Selected Vehicle IDs:", selectedOptions);
    setErrors((prevErrors) => ({
      ...prevErrors,
      submit: undefined,
    }));
    setSchedule((prevSchedule) => ({
      ...prevSchedule,
      vehicleIds: selectedOptions,
    }));
  };

  const handleReverseStops = () => {
    if (isViewMode || stopSchedules.length < 2) return;

    const { startTime } = getScheduleBoundaryTimes(stopSchedules, schedule);
    const reversed = reverseScheduleStopOrder(
      stopSchedules,
      segmentDurationsSeconds,
      startTime,
    );
    const nextStartTime = reversed.startTime || schedule.startTime;
    const nextEndTime = reversed.endTime || schedule.endTime;

    setSegmentDurationsSeconds(reversed.durations);
    setStopSchedules(
      buildResolvedStopSchedules(reversed.stops, {
        ...schedule,
        startTime: nextStartTime,
        endTime: nextEndTime,
      }),
    );
    setSchedule((prevSchedule) => ({
      ...prevSchedule,
      startTime: reversed.startTime || prevSchedule.startTime,
      endTime: reversed.endTime || prevSchedule.endTime,
    }));
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors, submit: undefined };
      Object.keys(nextErrors).forEach((key) => {
        if (key.startsWith("stop-")) delete nextErrors[key];
      });
      return nextErrors;
    });
    if (reversed.travelApplied) {
      setTravelTimeError("");
    }
  };

  const handleStopChange = (index, e) => {
    if (isViewMode) return;
    const { name, value } = e.target;
    const stopErrorKey = `stop-${index}-${name}`;
    setErrors((prevErrors) => ({
      ...prevErrors,
      [stopErrorKey]: undefined,
      submit: undefined,
    }));
    const updatedStops = [...stopSchedules];
    updatedStops[index] = { ...updatedStops[index], [name]: value };

    const isFirstStop = index === 0;
    const isLastStop = index === updatedStops.length - 1;

    if (
      isFirstStop &&
      (name === "departureTime" || name === "arrivalTime") &&
      /^\d{2}:\d{2}$/.test(value) &&
      canApplyTravelTimes(updatedStops, segmentDurationsSeconds)
    ) {
      try {
        const timeline = applyTravelDurationsToStopSchedules(
          updatedStops,
          value,
          segmentDurationsSeconds,
        );
        setStopSchedules(
          buildResolvedStopSchedules(timeline.stopSchedules, {
            ...schedule,
            startTime: value,
            endTime: timeline.endTime,
          }),
        );
        setSchedule((prevSchedule) => ({
          ...prevSchedule,
          startTime: value,
          endTime: timeline.endTime,
        }));
        setTravelTimeError("");
        return;
      } catch (error) {
        console.error("Error applying travel times:", error);
      }
    }

    const nextSchedule = {
      ...schedule,
      ...(isFirstStop && (name === "departureTime" || name === "arrivalTime")
        ? { startTime: value }
        : {}),
      ...(isLastStop && (name === "arrivalTime" || name === "departureTime")
        ? { endTime: value }
        : {}),
    };

    setStopSchedules(buildResolvedStopSchedules(updatedStops, nextSchedule));

    if (isFirstStop && (name === "departureTime" || name === "arrivalTime")) {
      setSchedule((prevSchedule) => ({ ...prevSchedule, startTime: value }));
    }

    if (isLastStop && (name === "arrivalTime" || name === "departureTime")) {
      setSchedule((prevSchedule) => ({ ...prevSchedule, endTime: value }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const lastStopIndex = stopSchedules.length - 1;
    const todayDateString = getTodayDateString();
    const { startTime, endTime } = getScheduleBoundaryTimes(
      stopSchedules,
      schedule,
    );
    const startTimeErrorKey =
      stopSchedules.length > 0 ? "stop-0-departureTime" : "startTime";
    const endTimeErrorKey =
      stopSchedules.length > 0
        ? `stop-${lastStopIndex}-arrivalTime`
        : "endTime";
    console.log("Validating form with schedule:", schedule);
    if (!schedule.name.trim()) newErrors.name = t("Schedule name is required");
    if (!schedule.repitation)
      newErrors.repitation = t("Repetition is required");
    if (!schedule.routeId) newErrors.routeId = t("Route is required");
    if (!schedule.startDate) newErrors.startDate = t("Start date is required");
    if (
      !effectiveScheduleId &&
      schedule.startDate &&
      schedule.startDate < todayDateString
    ) {
      newErrors.startDate = t("Start date cannot be in the past");
    }
    if (
      schedule.repitation === "WEEKLY" &&
      (!schedule.weeklyDays || schedule.weeklyDays.length === 0)
    ) {
      newErrors.weeklyDays = t("Select at least one day");
    }
    if (schedule.endCondition === "byDate" && !schedule.endDate) {
      newErrors.endDate = t("End date is required");
    }
    if (schedule.endCondition === "byDate" && schedule.endDate) {
      const minimumAllowedEndDate =
        schedule.startDate && schedule.startDate > todayDateString
          ? schedule.startDate
          : todayDateString;

      if (!effectiveScheduleId && schedule.endDate < minimumAllowedEndDate) {
        newErrors.endDate =
          minimumAllowedEndDate === schedule.startDate
            ? t("End date must be on or after the start date")
            : t("End date cannot be in the past");
      }
    }
    if (
      schedule.endCondition === "afterOccurrences" &&
      (!schedule.occurrences || Number(schedule.occurrences) < 1)
    ) {
      newErrors.occurrences = t("Occurrences must be at least 1");
    }
    const scheduleBaseDate = getLocalBaseDate(schedule.startDate);
    if (!startTime || !/^\d{2}:\d{2}$/.test(startTime)) {
      newErrors[startTimeErrorKey] = t("Valid start time is required (HH:mm)");
    }
    if (!endTime || !/^\d{2}:\d{2}$/.test(endTime)) {
      newErrors[endTimeErrorKey] = t("Valid end time is required (HH:mm)");
    }
    if (startTime && endTime) {
      const startMinutes = getTimelineMinutes(startTime);
      const endMinutes = getTimelineMinutes(endTime, startMinutes);
      if (
        startMinutes != null &&
        endMinutes != null &&
        endMinutes <= startMinutes
      ) {
        newErrors[endTimeErrorKey] = t("End time must be after start time");
      }
    }
    // For one-time schedules, disallow past start times
    if (
      schedule.repitation === "ONETIME" &&
      startTime &&
      /^\d{2}:\d{2}$/.test(startTime)
    ) {
      const now = new Date();
      const startDate = parseTimeToDate(startTime, scheduleBaseDate);
      if (startDate && startDate < now) {
        newErrors[startTimeErrorKey] = t(
          "Start time cannot be in the past for a one-time schedule",
        );
      }
    }
    // if (!schedule.description.trim()) newErrors.description = t('Description is required');
    if (stopSchedules.length > 0) {
      const resolvedStopSchedules = buildResolvedStopSchedules(
        stopSchedules,
        schedule,
      );

      stopSchedules.forEach((stop, index) => {
        const requiredFields = getRequiredStopTimeFields(
          index,
          stopSchedules.length,
        );

        if (requiredFields.includes("arrivalTime") && !stop.arrivalTime) {
          newErrors[`stop-${index}-arrivalTime`] = t(
            "Arrival time is required",
          );
        } else if (
          stop.arrivalTime &&
          !/^\d{2}:\d{2}$/.test(stop.arrivalTime)
        ) {
          newErrors[`stop-${index}-arrivalTime`] = t(
            "Valid time required (HH:mm)",
          );
        }

        if (requiredFields.includes("departureTime") && !stop.departureTime) {
          newErrors[`stop-${index}-departureTime`] = t(
            "Departure time is required",
          );
        } else if (
          stop.departureTime &&
          !/^\d{2}:\d{2}$/.test(stop.departureTime)
        ) {
          newErrors[`stop-${index}-departureTime`] = t(
            "Valid time required (HH:mm)",
          );
        }
      });

      let previousTimelineDepartureMinutes = null;

      resolvedStopSchedules.forEach((stop, index) => {
        const isFirstStop = index === 0;
        const isLastStop = index === stopSchedules.length - 1;
        const arrivalMinutes = getTimelineMinutes(
          stop.arrivalTime,
          previousTimelineDepartureMinutes,
        );
        const departureMinutes = getTimelineMinutes(
          stop.departureTime,
          arrivalMinutes,
        );

        if (
          !isFirstStop &&
          previousTimelineDepartureMinutes != null &&
          arrivalMinutes != null &&
          arrivalMinutes <= previousTimelineDepartureMinutes
        ) {
          newErrors[`stop-${index}-arrivalTime`] = t(
            "Must be after previous stop",
          );
        }

        if (
          !isFirstStop &&
          !isLastStop &&
          arrivalMinutes != null &&
          departureMinutes != null &&
          departureMinutes < arrivalMinutes
        ) {
          newErrors[`stop-${index}-departureTime`] = t(
            "Must be after arrival time",
          );
        }

        previousTimelineDepartureMinutes = departureMinutes;
      });
    }
    setErrors(newErrors);
    console.log("Validation errors:", newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    console.log("handleSubmit called with event:", e);
    e.preventDefault();

    if (isViewMode) {
      console.log("isViewMode is true, returning");
      return;
    }
    if (!validateForm()) {
      console.log("validateForm failed, returning");
      return;
    }

    const baseDate = getLocalBaseDate(schedule.startDate);
    baseDate.setHours(0, 0, 0, 0);
    const { startTime, endTime } = getScheduleBoundaryTimes(
      stopSchedules,
      schedule,
    );

    const formattedSchedule = buildSchedulePayload(
      schedule,
      startTime,
      endTime,
      baseDate,
    );
    const resolvedStopSchedules = buildResolvedStopSchedules(stopSchedules, {
      ...schedule,
      startTime,
      endTime,
    });
    const canSubmitStopSchedules = resolvedStopSchedules.every(
      (stopSchedule, index) =>
        hasCompleteStopSchedule(stopSchedule, index, resolvedStopSchedules),
    );

    if (stopSchedules.length > 0 && !canSubmitStopSchedules) {
      setLoading(false);
      setErrors((prevErrors) => ({
        ...prevErrors,
        submit: t("Please fill all required stop times"),
      }));
      return;
    }

    const localConflict = findScheduleConflictFromList(
      formattedSchedule,
      existingSchedulesRef.current,
      { ignoreScheduleId: effectiveScheduleId },
    );

    if (localConflict) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        submit: buildLocalScheduleConflictMessage(
          localConflict,
          t("Failed to submit schedule"),
        ),
      }));
      return;
    }

    setLoading(true);

    console.log("vehicleIds in schedule:", schedule.vehicleIds);
    console.log("formattedSchedule:", formattedSchedule);

    try {
      let createdSchedule;
      if (effectiveScheduleId) {
        console.log(formattedSchedule, "schedule");
        createdSchedule = await updateSchedule(formattedSchedule);
      } else {
        createdSchedule = await createSchedule(formattedSchedule);
      }

      const savedIds = resolvedStopSchedules.map((stopSchedule) =>
        Number(stopSchedule.id),
      );
      const hasSavedStops = savedIds.every(
        (id) => Number.isInteger(id) && id > 0,
      );

      if (
        stopSchedules.length > 0 &&
        hasSavedStops &&
        canSubmitStopSchedules
      ) {
        console.log(resolvedStopSchedules, "resolvedStopSchedules");
        const formattedStopSchedules = resolvedStopSchedules.map(
          (stopSchedule, index) => {
            const normalizedTimes = getNormalizedStopTimes(
              stopSchedule,
              index,
              resolvedStopSchedules,
            );

            return {
              id: stopSchedule.id,
              ...normalizedTimes,
              stopId: stopSchedule.stopId,
            };
          },
        );
        console.log(formattedStopSchedules, "formattedStopSchedules");
        await updateStopSchedules(formattedStopSchedules);
      } else if (stopSchedules.length > 0 && canSubmitStopSchedules) {
        const formattedStopSchedules = resolvedStopSchedules.map(
          (stopSchedule, index) => {
            const normalizedTimes = getNormalizedStopTimes(
              stopSchedule,
              index,
              resolvedStopSchedules,
            );

            return {
              ...normalizedTimes,
              stopId: stopSchedule.stopId,
            };
          },
        );
        await createStopSchedules(createdSchedule.id, formattedStopSchedules);
      }

      setTimeout(() => {
        finishForm();
      }, 1500);
    } catch (error) {
      console.error(error);
      const submitMessage =
        getApiErrorResponse(error).code === "SCHEDULE_CONFLICT"
          ? buildScheduleConflictMessage(error, t("Failed to submit schedule"))
          : getGenericApiErrorMessage(error, t("Failed to submit schedule"));

      setErrors((prevErrors) => ({
        ...prevErrors,
        submit: submitMessage,
      }));
    } finally {
      setLoading(false);
    }
  };

  const getAvailableVehicles = () => {
    if (!vehicles || vehicles.length === 0) return [];
    const currentRoute =
      routes.find((route) => Number(route.id) === Number(schedule.routeId)) ||
      selectedRoute;
    const routeVehicleIds = getRouteAssignedVehicleIds(currentRoute, vehicles);

    return vehicles.filter((vehicle) => {
      const normalizedVehicleId = Number(vehicle.id);
      return routeVehicleIds.has(normalizedVehicleId);
    });
  };

  const formBody = (
    <Box>
        {!embedded && (
        <SubTitle
          title={
            effectiveScheduleId
              ? t("Update Schedule")
              : updateRoute
                ? t("Assign Schedules")
                : t("Create Schedules")
          }
          path={path}
          breadcrumbHandlers={{
            Update: location.state?.rowData
              ? { to: "/route/add", state: { rowData: location.state.rowData } }
              : null,
          }}
        />
        )}
        <Box sx={{ mx: embedded ? 0 : "40px", p: 0 }}>
          <Paper
            elevation={4}
            sx={{
              p: 2,
              width: "100%",
              borderRadius: 2,
              border: "1px solid #A5DCF3",
              position: "relative",
              padding: "40px",
              boxShadow: "-2px 2px 5px 0px #00000040",
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: "500",
                  fontSize: "20px",
                  fontFamily: "Roboto",
                  height: "50px",
                  background: "#647490",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                  borderRadius: "8px",
                  textAlign: "center",
                  padding: "24px 12px",
                  color: "#FFFFFF",
                  boxShadow: "-2px 2px 5px 0px #00000040",
                }}
              >
                {isViewMode ? t("View Schedule Details") : t("scheduleDetails")}
                {location.state?.id?.name
                  ? ` for ${location.state.id.name}`
                  : ""}
              </Typography>
              <ScheduleDetailsForm
                schedule={schedule}
                routes={routes}
                // vehicles={vehicles}
                vehicles={getAvailableVehicles()}
                errors={errors}
                handleChange={handleChange}
                handleVehicleChange={handleVehicleChange}
                isViewMode={isViewMode}
                updateRoute={updateRoute}
              />
            </Box>
          </Paper>
          <Paper
            elevation={4}
            sx={{
              p: 2,
              mt: "20px",
              width: "100%",
              borderRadius: 2,
              border: "1px solid #A5DCF3",
              position: "relative",
              padding: "40px",
              boxShadow: "-2px 2px 5px 0px #00000040",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: "500",
                fontSize: "20px",
                fontFamily: "Roboto",
                height: "50px",
                background: "#647490",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "8px",
                textAlign: "center",
                padding: "24px 12px",
                color: "#FFFFFF",
                boxShadow: "-2px 2px 5px 0px #00000040",
              }}
            >
              {t("Trip Time Table")}
              {!isViewMode && stopSchedules.length > 1 && (
                <Button
                  type="button"
                  size="small"
                  onClick={handleReverseStops}
                  sx={{
                    minWidth: 0,
                    px: 1.25,
                    py: 0.4,
                    color: "#1F242E",
                    backgroundColor: "#fff",
                    textTransform: "none",
                    fontSize: "12px",
                    lineHeight: "16px",
                    borderRadius: "6px",
                    "&:hover": { backgroundColor: "#f8fafc" },
                  }}
                >
                  {t("Reverse stops")}
                </Button>
              )}
            </Typography>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  mb: 3,
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Typography
                    sx={{
                      color: "#111214",
                      fontFamily: "Roboto",
                      fontWeight: 600,
                      fontStyle: "SemiBold",
                      fontSize: "16px",
                      leadingTrim: "NONE",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                    }}
                  >
                    
                    {t("Start Time")}:
                  </Typography>
                  <Typography
                    sx={{
                      color: "#414958",
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      fontStyle: "Medium",
                      fontSize: "16px",
                      leadingTrim: "NONE",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                    }}
                  >
                    {boundaryStartTime
                      ? parseTimeToDate(boundaryStartTime).toLocaleTimeString(
                          "en-IN",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          },
                        )
                      : "--:-- AM/PM"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Typography
                    sx={{
                      color: "#111214",
                      fontFamily: "Roboto",
                      fontWeight: 600,
                      fontStyle: "SemiBold",
                      fontSize: "16px",
                      leadingTrim: "NONE",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                    }}
                  >
                    {t("End Time")}:
                  </Typography>
                  <Typography
                    sx={{
                      color: "#414958",
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      fontStyle: "Medium",
                      fontSize: "16px",
                      leadingTrim: "NONE",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                    }}
                  >
                    {boundaryEndTime
                      ? parseTimeToDate(boundaryEndTime).toLocaleTimeString(
                          "en-IN",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          },
                        )
                      : "--:-- AM/PM"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Typography
                    sx={{
                      color: "#111214",
                      fontFamily: "Roboto",
                      fontWeight: 600,
                      fontStyle: "SemiBold",
                      fontSize: "16px",
                      leadingTrim: "NONE",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                    }}
                  >
                    {t("Trip Duration")}:
                  </Typography>
                  <Typography
                    sx={{
                      color: "#414958",
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      fontStyle: "Medium",
                      fontSize: "16px",
                      leadingTrim: "NONE",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                    }}
                  >
                    {boundaryStartTime && boundaryEndTime
                      ? `${getTripDurationMinutes(
                          boundaryStartTime,
                          boundaryEndTime,
                        )} minutes`
                      : "--:-- AM/PM"}
                  </Typography>
                </Box>
              </Box>
              {travelTimeError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {travelTimeError}
                </Alert>
              )}
              {stopSchedules.length > 0 && (
                <StopSchedulesTable
                  stopSchedules={stopSchedules}
                  errors={errors}
                  handleStopChange={handleStopChange}
                  isViewMode={isViewMode}
                  onCancel={closeForm}
                  onSave={handleSubmit}
                />
              )}
              {/* <Divider sx={{ my: 3 }} /> */}
              <Box sx={{ mt: 3 }}>
                <ActionButtons
                  loading={loading}
                  scheduleId={effectiveScheduleId}
                  onCancel={closeForm}
                  onSave={handleSubmit}
                  onDelete={embedded && effectiveScheduleId ? onDelete : undefined}
                  isViewMode={isViewMode}
                />
              </Box>
              {errors.submit && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.submit}
                </Alert>
              )}
            </Box>
          </Paper>
        </Box>
        {!embedded && <Box sx={{ height: "10vh" }}></Box>}
    </Box>
  );

  if (embedded) {
    return formBody;
  }

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: "#f5f5f5",
        minHeight: "100vh",
        minWidth: "100vw",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <TitleBar />
        <NavigationMenu />
        {formBody}
        <Footer />
      </Box>
    </Box>
  );
};

export default AddSchedule;
